import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  TextField,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import BlogPostService, { BlogLocale } from '../../services/blog-post.service';
import branding from '../../config/branding';

const apiBase =
  (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');

function coverThumbUrl(attributes: Record<string, any>): string | null {
  const cover = attributes?.cover?.data?.attributes?.url ?? attributes?.cover?.data?.url;
  if (!cover || typeof cover !== 'string') return null;
  if (cover.startsWith('http')) return cover;
  return `${apiBase}${cover}`;
}

const BlogPostList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [locale, setLocale] = useState<BlogLocale>('en');
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await BlogPostService.getAll(locale);
      setPosts(res.data?.data || []);
    } catch (error) {
      console.error(error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('marketing.blog.confirmDelete'))) return;
    try {
      await BlogPostService.delete(id);
      fetchPosts();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" mb={3}>
        <Typography variant="h4">{t('marketing.blog.title')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
          <TextField select size="small" label={t('marketing.blog.locale')} value={locale} onChange={(e) => setLocale(e.target.value as BlogLocale)} sx={{ minWidth: 120 }}>
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="ar">العربية</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/marketing/blog/create')}>
            {t('marketing.blog.newPost')}
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('marketing.blog.erpSubtitle', { domain: branding.apiUrl })}
      </Typography>

      {loading ? (
        <Typography>{t('common.loading')}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('marketing.blog.cover')}</TableCell>
                <TableCell>{t('marketing.blog.articleTitle')}</TableCell>
                <TableCell>{t('marketing.blog.slugColumn')}</TableCell>
                <TableCell>{t('marketing.blog.seoTitleShort')}</TableCell>
                <TableCell align="right">{t('marketing.blog.viewsColumn')}</TableCell>
                <TableCell>{t('marketing.blog.statusColumn')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((row) => {
                const attrs = row.attributes ?? row;
                const pub = attrs.publishedAt;
                const url = coverThumbUrl(attrs);
                return (
                  <TableRow key={row.id}>
                    <TableCell sx={{ width: 72 }}>
                      {url ? (
                        <Box
                          component="img"
                          src={url}
                          alt=""
                          sx={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 1 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{attrs.title}</Typography>
                      {(attrs.excerpt as string)?.slice(0, 80)}
                      {(attrs.excerpt?.length ?? 0) > 80 ? '…' : ''}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="code">
                        {attrs.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{attrs.seoTitle || '—'}</TableCell>
                    <TableCell align="right">{attrs.viewCount ?? 0}</TableCell>
                    <TableCell>
                      <Chip label={pub ? t('marketing.blog.statusPublished') : t('marketing.blog.statusDraft')} size="small" color={pub ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('marketing.blog.otherLocale')}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/marketing/blog/create?basedOn=${row.id}&locale=${locale === 'en' ? 'ar' : 'en'}`,
                            )
                          }
                        >
                          <TranslateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => navigate(`/marketing/blog/${row.id}/edit`, { state: { locale } })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(Number(row.id))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && posts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('marketing.blog.empty')}</Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/marketing/blog/create')}>
            {t('marketing.blog.newPost')}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default BlogPostList;
